<script lang="ts">
import { watch, ref } from "vue";
import { BNumberinput } from "buefy";
import useEmptyNumberJumpClamp from "@/composable/components/useEmptyNumberJumpClamp";

export default {
  name: "InputNumberInterval",
  components: { BNumberinput },
  props: {
    inputType: {
      type: String,
      required: true,
    },
    format: {
      type: String,
      required: true,
    },
    // #476 P4 - Accepter null (champ vide au chargement)
    from: {
      type: [Number, null],
      default: null,
    },
    to: {
      type: [Number, null],
      default: null,
    },
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
  },
  emits: ["update:numberValueInterval", "update:validity"],
  setup(props, ctx) {
    // #476 P4 - Initialiser a null pour afficher le placeholder
    const fromValueNumber = ref<number | null>(null);
    const toValueNumber = ref<number | null>(null);
    const isInvalid = ref(false);
    const fromMissing = ref(false);
    const toMissing = ref(false);

    watch(() => props.from, (value) => { fromValueNumber.value = value; });
    watch(() => props.to, (value) => { toValueNumber.value = value; });

    useEmptyNumberJumpClamp(fromValueNumber, () => props.min, () => props.max);
    useEmptyNumberJumpClamp(toValueNumber, () => props.min, () => props.max);
    let validityTimer: ReturnType<typeof setTimeout> | null = null;

    // #476 - Verifie la coherence de l'intervalle et emet si valide
    function checkAndEmit() {
      const fromFilled = fromValueNumber.value !== null && isFinite(fromValueNumber.value);
      const toFilled = toValueNumber.value !== null && isFinite(toValueNumber.value);

      // Indiquer quel champ manque quand l'intervalle est incomplet
      fromMissing.value = !fromFilled && toFilled;
      toMissing.value = fromFilled && !toFilled;
      const bothEmpty = !fromFilled && !toFilled;
      if (!fromFilled || !toFilled) {
        isInvalid.value = false;
        let reasonKey = "";
        if (fromMissing.value) reasonKey = "dataTypeAuthorizations.field-from-required";
        else if (toMissing.value) reasonKey = "dataTypeAuthorizations.field-to-required";
        ctx.emit("update:validity", { valid: bothEmpty, reasonKey });
        return;
      }
      // Verifier la coherence min <= max
      if (fromValueNumber.value! > toValueNumber.value!) {
        isInvalid.value = true;
        ctx.emit("update:validity", {
          valid: false,
          reasonKey: "dataTypeAuthorizations.value-from-must-be-less-than-to",
        });
        return;
      }
      isInvalid.value = false;
      ctx.emit("update:validity", { valid: true });
      ctx.emit("update:numberValueInterval", {
        interval: { from: fromValueNumber.value, to: toValueNumber.value },
      });
    }

    // #476 - Validation de coherence en temps reel avec debounce (200ms)
    // Couvre la saisie clavier et les clics sur +/-
    watch([fromValueNumber, toValueNumber], () => {
      if (validityTimer) clearTimeout(validityTimer);
      validityTimer = setTimeout(() => checkAndEmit(), 200);
    });

    return {
      fromValueNumber,
      toValueNumber,
      checkAndEmit,
      isInvalid,
      fromMissing,
      toMissing,
    };
  },
};
</script>

<template>
  <div class="rows">
    <div class="row">
      <span class="label">{{ $t("dataTypeAuthorizations.from") }}</span>
    </div>
    <b-numberinput
      v-model="fromValueNumber"
      :max="max"
      :min="min"
      :placeholder="format"
      controls-position="compact"
      controls-rounded
      :step="format === 'float' ? 0.1 : 1"
      :min-step="format === 'float' ? 0.00001 : 1"
      type="is-primary is-light"
      @blur="checkAndEmit"
    />
    <div class="row">
      <span class="label">{{ $t("dataTypeAuthorizations.to") }}</span>
    </div>
    <b-numberinput
      v-model="toValueNumber"
      :max="max"
      :min="min"
      :placeholder="format"
      controls-position="compact"
      controls-rounded
      :step="format === 'float' ? 0.1 : 1"
      :min-step="format === 'float' ? 0.00001 : 1"
      type="is-primary is-light"
      @blur="checkAndEmit"
    />
    <p
      v-if="fromMissing"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.field-from-required") }}
    </p>
    <p
      v-if="toMissing"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.field-to-required") }}
    </p>
    <p
      v-if="isInvalid"
      class="help is-danger"
    >
      {{ $t("dataTypeAuthorizations.value-from-must-be-less-than-to") }}
    </p>
  </div>
</template>
